import React from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';

import Typography from '@material-ui/core/Typography';

import MaterialTable from 'components/Table/Table';
//import { ViewMode } from '../viewModes';
//import { Currency } from 'enums';
import { ExternalProductData, ExtProductCategory, ExtProductFreq, ExtProductUsage } from './model';
import { ExternalProductsStore } from 'stores/workflowStores/finances/externalProducts/externalProducts';
import {
  compareSqlDates,
  formatAmountValue,
  fractionToPercent,
  pluralize,
  searchInStrings,
  sqlDateToLabel,
} from 'helpers/helpers';
import { EdwardColors, Order } from 'enums';
import IconButton from '@material-ui/core/IconButton/IconButton';
import EditIcon from '@material-ui/icons/EditOutlined';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import { getUpdateColor } from './ExternalProduct';
import Grid from '@material-ui/core/Grid/Grid';
import { MTableToolbar } from '@material-table/core';
import Tooltip from '@material-ui/core/Tooltip/Tooltip';
import FilterIcon from '../../../../../components/Icons/FilterIcon';
import Button from '@material-ui/core/Button/Button';
import GetAppIcon from '@material-ui/icons/GetApp';

interface Props {
  readOnly?: boolean;
  userMode?: boolean;
  original?: boolean;
  data?: Partial<ExternalProductData>[];
  store?: ExternalProductsStore;
  editProduct?: (productId: string) => void;
  deleteProduct?: (productId: string) => void;
  pdf?: boolean;
  header: boolean;
  filtersVisible?: boolean;
  toggleFilterVisibility?: () => void;
}
/*
const preSort = {
  [ExtProductCategory.NONE]: 0, // used for current account
  [ExtProductCategory.EDWARD]: 1,
  [ExtProductCategory.FINANCIAL_PRODUCT]: 2,
  [ExtProductCategory.PROPERTY]: 3,
  [ExtProductCategory.LOAN]: 4,
};
*/
const ExternalProductsTable: React.FC<Props> = ({
  data,
  readOnly,
  userMode,
  store,
  editProduct,
  deleteProduct,
  original,
  pdf,
  header,
  filtersVisible,
  toggleFilterVisibility,
}) => {
  const { t } = useTranslation();

  const handleDownloadProducts = async () => {
    if (store) await store.downloadProducts();
  };

  const prepData = <T extends any>(columns: T[]): T[] => {
    return store ? store.sortTable(columns) : columns;
  };

  //TODO: remove PDF

  return data ? (
    <MaterialTable
      className={pdf ? '_extProd _extProdPdf' : '_extProd'}
      enhanced
      options={{
        //paginationPosition
        header: header,
        sorting: !pdf,
        draggable: false,
        search: false,
        toolbar: !pdf,
        paging: false,
        emptyRowsWhenPaging: false,
        showEmptyDataSourceMessage: false,
        defaultExpanded: true,
        rowStyle: (rowData, index, level) => {
          return rowData.id !== 'summary' ? { backgroundColor: '#fff' } : {};
        },
        filtering: filtersVisible,
      }}
      style={pdf ? { width: '100%' } : { marginTop: header ? '-62px' : 0 }}
      onOrderChange={
        store
          ? (orderedColumnId, orderDirection) => store.onTableOrderChange(orderedColumnId, orderDirection as Order)
          : undefined
      }
      parentChildData={(row, rows) => rows.find((a) => a.id === row.parentId)}
      data={data || []}
      //footerData={[{ title: 'Celkem', }]}
      components={
        header
          ? {
              Toolbar: (props) => (
                <Grid container>
                  <MTableToolbar {...props} />
                  <div>
                    <Tooltip title={t('general_show_filters') || ''} placement="top">
                      <div>
                        <IconButton size="small" onClick={toggleFilterVisibility}>
                          <FilterIcon transparentBgr defaultFill fontSize="large" strokeWidth={0.1} />
                        </IconButton>
                      </div>
                    </Tooltip>
                    <Button variant="outlined" onClick={handleDownloadProducts} startIcon={<GetAppIcon />}>
                      {t('accounts_download_csv_short')}
                    </Button>
                  </div>
                </Grid>
              ),
            }
          : {
              Toolbar: (props) => (
                <Grid container style={{ pointerEvents: 'none' }}>
                  <MTableToolbar {...props} />
                </Grid>
              ),
            }
      }
      columns={prepData([
        {
          field: 'title',
          title: t('general_title'),
          render: ({ title, parentId, id }) =>
            id === 'summary' ? (
              <Typography variant="h4" style={{ fontSize: pdf ? '14px' : '18px', marginBottom: 0, lineHeight: '48px' }}>
                {t(`externalProduct_table_sum`)}
              </Typography>
            ) : (
              <div style={{ minHeight: '40px', display: 'inline-flex', alignItems: 'center' }}>{title}</div>
            ),
          customSort: (a, b) => {
            //const categorySort = preSort[a.category as ExtProductCategory] - preSort[b.category as ExtProductCategory];
            //return categorySort === 0 ? a.title.localeCompare(b.title) : categorySort;
            return a.title.localeCompare(b.title);
          },
        },
        {
          field: 'type',
          title: t('externalProduct_type'),
          render: ({ type }) => (type ? t(`externalProduct_type_${type}`) : ''),
          customSort: (a, b) => {
            return t(`externalProduct_type_${a.type}`).localeCompare(t(`externalProduct_type_${b.type}`));
          },
          customFilterAndSearch: (filter, rowData, def) => {
            const value = !!rowData.type ? t(`externalProduct_type_${rowData.type}`) : '';
            return searchInStrings(filter, [value]);
          },
          hidden: !!pdf,
        },
        { field: 'financialInstitution.title', title: t('externalProduct_financialInstitution_short') },
        {
          field: 'balance.value',
          title: t('accountPlan_currentValue'),
          cellStyle: { textAlign: 'right' },
          headerStyle: { textAlign: 'right' },
          filtering: false,
          render: ({ balance, category, id }) => (
            <span style={id === 'summary' ? { fontWeight: 'bold', fontSize: pdf ? '14px' : '18px' } : undefined}>
              {balance
                ? formatAmountValue(
                    Math.round(balance.value) * (category === ExtProductCategory.LOAN ? -1 : 1),
                    balance.currency,
                  )
                : ''}
            </span>
          ),
          customSort: (a, b) => {
            return (
              ((a.balance?.value || 0) * (a.category === ExtProductCategory.LOAN ? -1 : 1)) -
              ((b.balance?.value || 0) * (b.category === ExtProductCategory.LOAN ? -1 : 1))
            );
          },
        },
        {
          field: 'periodic',
          title: t('externalProduct_periodic_short'),
          filtering: false,
          customSort: (a, b) => {
            return (
              (a.periodic?.value.value || 0) +
              (a.employer?.value.value || 0) +
              (a.government?.value.value || 0) -
              ((b.periodic?.value.value || 0) + (b.employer?.value.value || 0) + (b.government?.value.value || 0))
            );
          },
          render: ({ periodic, employer, government, id, category }) =>
            !periodic ? (
              ''
            ) : (
              <span style={id === 'summary' ? { fontWeight: 'bold', fontSize: pdf ? '14px' : '18px' } : undefined}>
                {periodic.value.value > 0 && (
                  <>
                    {formatAmountValue(Math.round(periodic.value.value), periodic.value.currency)}{' '}
                    {!periodic.aggregated && category !== ExtProductCategory.LOAN && (
                      <>({t('general_client').toLowerCase()})</>
                    )}
                  </>
                )}
                {employer && employer.value.value > 0 && (
                  <>
                    <br />
                    {formatAmountValue(Math.round(employer.value.value), employer.value.currency)} (
                    {t('general_employer').toLowerCase()})
                  </>
                )}
                {government && government.value.value > 0 && (
                  <>
                    <br />
                    {formatAmountValue(Math.round(government.value.value), government.value.currency)} (
                    {t('externalProduct_government_short').toLowerCase()})
                  </>
                )}
              </span>
            ),
        },
        {
          field: 'interest',
          title: t('bucket_calculatedOutcome'),
          filtering: false,
          render: ({ interest }) => (interest !== undefined ? fractionToPercent(interest) : ''),
        },
        {
          field: 'lastUpdate',
          title: t('externalProduct_lastUpdate'),
          filtering: false,
          render: ({ lastUpdate, invalidLink, financialInstitution, accountId }) =>
            lastUpdate || invalidLink ? (
              <span
                style={{
                  color: original ? '#333' : invalidLink ? EdwardColors.ALERT : getUpdateColor(lastUpdate),
                }}
              >
                {invalidLink
                  ? t('externalProduct_account_invalid')
                  : financialInstitution?.internal && accountId
                  ? t('externalProduct_lastUpdate_daily')
                  : sqlDateToLabel(lastUpdate, 'dd.MM.yyyy')}
              </span>
            ) : (
              ''
            ),
          hidden: !!pdf,
        },
        {
          field: 'usage',
          title: t('externalProduct_usage'),
          customSort: (a, b) => {
            return (
              a.frequency === ExtProductFreq.INFINITE
                ? t(`externalProduct_freq_${ExtProductFreq.INFINITE}`)
                : t(`externalProduct_usage_${a.usage}`)
            ).localeCompare(
              b.frequency === ExtProductFreq.INFINITE
                ? t(`externalProduct_freq_${ExtProductFreq.INFINITE}`)
                : t(`externalProduct_usage_${b.usage}`),
            );
          },
          render: ({ usage, category, frequency }) =>
            usage && category !== ExtProductCategory.LOAN
              ? frequency === ExtProductFreq.INFINITE
                ? t(`externalProduct_freq_${ExtProductFreq.INFINITE}`)
                : t(`externalProduct_usage_${usage}`)
              : '',

          customFilterAndSearch: (filter, rowData, def) => {
            const value =
              rowData.usage && rowData.category !== ExtProductCategory.LOAN
                ? rowData.frequency === ExtProductFreq.INFINITE
                  ? t(`externalProduct_freq_${ExtProductFreq.INFINITE}`)
                  : t(`externalProduct_usage_${rowData.usage}`)
                : '';
            return searchInStrings(filter, [value]);
          },
          /*
          lookup: {
            [ExtProductUsage.TARGET]: t('externalProduct_usage_TARGET'),
            [ExtProductUsage.EXTERNAL]: t('externalProduct_usage_EXTERNAL'),
            [ExtProductUsage.NONE]: t('externalProduct_usage_NONE'),
            [ExtProductFreq.INFINITE]: t('externalProduct_freq_INFINITE'),
            [ExtProductCategory.LOAN]: t('externalProducts_title_loan'),
          },
          */
        },
        {
          field: 'frequency',
          title: t('general_frequency'),
          //filtering: false,
          /*
          customSort: (a, b) => {
            return compareSqlDates(
              a.frequency === ExtProductFreq.INFINITE || a.usage === ExtProductUsage.NONE
                ? '3000-01-01'
                : a.since || a.periodic?.since,
              b.frequency === ExtProductFreq.INFINITE || b.usage === ExtProductUsage.NONE
                ? '3000-01-01'
                : b.since || b.periodic?.since,
            );
          },
          */
          customFilterAndSearch: (filter, rowData, def) => {
            const value =
              rowData.frequency &&
              rowData.frequency !== ExtProductFreq.INFINITE &&
              rowData.usage !== ExtProductUsage.NONE
                ? t(`externalProduct_freq_${rowData.frequency}`)
                : '';

            return searchInStrings(filter, [value]);
          },
          render: ({ frequency, usage, repeatYears }) =>
            frequency ? (
              <>
                {frequency !== ExtProductFreq.INFINITE && usage !== ExtProductUsage.NONE && (
                  <>
                    {t(`externalProduct_freq_${frequency}`)}
                    <br />
                    {frequency === ExtProductFreq.REPEAT_EVERY && (
                      <>
                        {' '}
                        {repeatYears}
                        {' '}
                        {pluralize(repeatYears, t('general_repeatYear'), t('general_repeatYears'))}
                      </>
                    )}
                  </>
                )}
              </>
            ) : (
              ''
            ),
        },
        {
          field: 'until',
          title: t('externalProduct_table_until'),
          filtering: false,
          customSort: (a, b) => {
            return compareSqlDates(
              a.frequency === ExtProductFreq.INFINITE || a.usage === ExtProductUsage.NONE
                ? '3000-01-01'
                : a.since || a.periodic?.since,
              b.frequency === ExtProductFreq.INFINITE || b.usage === ExtProductUsage.NONE
                ? '3000-01-01'
                : b.since || b.periodic?.since,
            );
          },
          render: ({ frequency, usage, repeatYears, since, until, periodic }) =>
            frequency ? (
              <>
                {frequency !== ExtProductFreq.INFINITE && usage !== ExtProductUsage.NONE && (
                  <>
                    {![
                      ExtProductFreq.MONTHLY,
                      ExtProductFreq.YEARLY,
                      ExtProductFreq.REPEAT_EVERY,
                      ExtProductFreq.PENSION,
                      ExtProductFreq.ADVANCE_PENSION,
                    ].includes(frequency) ? (
                      <>{sqlDateToLabel(since)}</>
                    ) : (
                      <>
                        {t('general_since')}: {sqlDateToLabel(since || periodic.since)}
                      </>
                    )}
                  </>
                )}
              </>
            ) : (
              ''
            ),
        },
        {
          field: 'amount',
          title: t('general_amount'),
          headerStyle: { textAlign: 'right' },
          cellStyle: { textAlign: 'right' },
          filtering: false,
          render: ({ frequency, usage, value }) =>
            value && value.value > 0 ? formatAmountValue(value.value, value.currency) : '',
          customSort: (a, b) => {
            return (a.value?.value || 0) - (b.value?.value || 0);
          },
        },
        {
          field: 'actions',
          //cellStyle: { width: '100px' },
          filtering: false,
          render: ({ id }) =>
            id === 'summary' || id === 'current' ? (
              ''
            ) : (
              <>
                {editProduct && deleteProduct && !userMode && (
                  <>
                    <IconButton
                      size="medium"
                      className="account-delete-button"
                      onClick={() => editProduct(id)}
                      disabled={readOnly}
                    >
                      <EditIcon fontSize="inherit" />
                    </IconButton>

                    <IconButton
                      size="medium"
                      className="account-delete-button"
                      onClick={() => deleteProduct(id)}
                      disabled={readOnly}
                    >
                      <DeleteIcon fontSize="inherit" />
                    </IconButton>
                  </>
                )}
              </>
            ),
          hidden: !!pdf,
        },
      ])}
    />
  ) : null;
};

export default observer(ExternalProductsTable);
